import React from 'react';
import { Icon, Popup } from 'semantic-ui-react';
import GlassmorphismTheme from '../../styles/glassmorphism';

interface MetricCardProps {
  title: string;
  value: string | number;
  icon: string;
  color?: string;
  trend?: number;
  trendLabel?: string;
  subtitle?: string;
  tooltip?: string;
  onClick?: () => void;
  clickable?: boolean;
  /** Destaque quando o card representa o filtro ativo (ex.: KPI ou status selecionado). */
  active?: boolean;
}

const titleStyle: React.CSSProperties = {
  margin: 0,
  color: GlassmorphismTheme.colors.solid.textLight,
  fontSize: '0.8rem',
  fontWeight: 500,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  lineHeight: 1.3,
  textAlign: 'center',
};

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  icon,
  color = '#3498db',
  trend,
  trendLabel,
  subtitle,
  tooltip,
  onClick,
  clickable = false,
  active = false,
}) => {
  const trendColor = trend !== undefined ? (trend >= 0 ? '#2ecc71' : '#e74c3c') : undefined;
  const trendIcon = trend !== undefined ? (trend >= 0 ? 'arrow up' : 'arrow down') : undefined;

  const cardStyle: React.CSSProperties = {
    ...GlassmorphismTheme.components.glassCard,
    height: '100%',
    minHeight: 168,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: GlassmorphismTheme.spacing.md,
    textAlign: 'center',
    transition: GlassmorphismTheme.transitions.normal,
    border: active ? `2px solid ${color}` : `1px solid ${color}33`,
    background: active ? `${color}12` : '#ffffff',
    boxShadow: active ? `0 4px 16px ${color}44` : '0 2px 8px rgba(0, 0, 0, 0.1)',
    cursor: clickable ? 'pointer' : 'default',
  };

  const renderTitle = (asHeading: boolean) => {
    const TitleTag = asHeading ? 'h4' : 'span';
    const titleBlock = (
      <TitleTag
        style={{
          ...titleStyle,
          ...(tooltip
            ? {
                cursor: 'help',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
              }
            : {}),
        }}
      >
        {title}
        {tooltip ? <Icon name="info circle" size="tiny" style={{ opacity: 0.6, flexShrink: 0 }} /> : null}
      </TitleTag>
    );

    if (tooltip) {
      return (
        <Popup
          trigger={
            <div
              style={{
                minHeight: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: GlassmorphismTheme.spacing.xs,
                width: '100%',
              }}
            >
              {titleBlock}
            </div>
          }
          content={tooltip}
          position="top center"
          size="small"
          style={{ zIndex: 10000 }}
        />
      );
    }

    return (
      <div
        style={{
          minHeight: 40,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: GlassmorphismTheme.spacing.xs,
          width: '100%',
        }}
      >
        {titleBlock}
      </div>
    );
  };

  const innerStatic = (asHeading: boolean) => (
    <>
      <div style={{ marginBottom: GlassmorphismTheme.spacing.sm }}>
        <Icon
          name={icon as any}
          size="large"
          style={{
            color: color,
            filter: 'drop-shadow(0 4px 8px rgba(176, 196, 222, 0.2))',
          }}
        />
      </div>

      {renderTitle(asHeading)}

      <div
        style={{
          fontSize: '2rem',
          fontWeight: 700,
          color: color,
          marginBottom: GlassmorphismTheme.spacing.xs,
          lineHeight: 1.1,
          width: '100%',
          wordBreak: 'break-word',
        }}
      >
        {typeof value === 'number' ? value.toLocaleString('pt-BR') : value}
      </div>

      <div style={{ minHeight: subtitle ? undefined : 0, width: '100%' }}>
        {subtitle && (
          <p
            style={{
              margin: 0,
              color: GlassmorphismTheme.colors.solid.textLight,
              fontSize: '0.85rem',
              lineHeight: 1.35,
            }}
          >
            {subtitle}
          </p>
        )}
      </div>

      {trend !== undefined && trendLabel && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px',
            marginTop: GlassmorphismTheme.spacing.xs,
            padding: `${GlassmorphismTheme.spacing.xs} ${GlassmorphismTheme.spacing.sm}`,
            background: trendColor ? `${trendColor}20` : 'transparent',
            borderRadius: GlassmorphismTheme.borderRadius.sm,
            fontSize: '0.8rem',
            fontWeight: 600,
          }}
        >
          <Icon name={trendIcon as any} style={{ color: trendColor }} />
          <span style={{ color: trendColor }}>{Math.abs(trend)}%</span>
          <span style={{ color: GlassmorphismTheme.colors.solid.textLight, marginLeft: '4px' }}>
            {trendLabel}
          </span>
        </div>
      )}
    </>
  );

  const innerClickable = innerStatic(false);

  const hoverHandlers = {
    onMouseEnter: (e: React.MouseEvent<HTMLElement>) => {
      (e.currentTarget as HTMLElement).style.transform = 'translateY(-4px)';
      (e.currentTarget as HTMLElement).style.boxShadow = `0 12px 40px ${color}33`;
    },
    onMouseLeave: (e: React.MouseEvent<HTMLElement>) => {
      (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
      (e.currentTarget as HTMLElement).style.boxShadow = active
        ? `0 4px 16px ${color}44`
        : GlassmorphismTheme.shadows.glassSoft;
    },
  };

  if (clickable && onClick) {
    return (
      <button
        type="button"
        style={{
          ...cardStyle,
          width: '100%',
          font: 'inherit',
          appearance: 'none',
        }}
        onClick={onClick}
        aria-pressed={active}
        aria-label={`${title}: ${value}`}
        {...hoverHandlers}
      >
        {innerClickable}
      </button>
    );
  }

  return (
    <div
      style={cardStyle}
      onClick={clickable ? onClick : undefined}
      aria-pressed={clickable ? active : undefined}
      {...hoverHandlers}
    >
      {innerStatic(true)}
    </div>
  );
};

export default MetricCard;
